/**
 * Hook to validate auth session on app load and periodically
 * 
 * This hook ensures user data stays in sync with the backend:
 * - Validates session immediately on app load
 * - Refreshes user data every 5 minutes
 * - Updates user state (like email_verified) without requiring manual refresh
 */
import { useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '../store';
import { authAPI } from '../services/api';

export const useAuthValidation = () => {
  const { token, user, logout, login } = useAuthStore();
  const validationRef = useRef(false);
  const intervalRef = useRef(null);

  const validateSession = useCallback(async () => {
    if (!token) return false;
    
    try {
      const response = await authAPI.getMe();
      if (response.data) {
        // Always update user data to ensure we have the latest state
        // This catches changes like email_verified, role updates, etc.
        const currentUserStr = JSON.stringify(user);
        const newUserStr = JSON.stringify(response.data);
        
        if (currentUserStr !== newUserStr) {
          console.log('[Auth] User data updated from server');
          login(response.data, token);
        }
        return true;
      }
      return false;
    } catch (error) {
      if (error.response?.status === 401) {
        console.log('[Auth] Session expired, logging out...');
        logout();
        return false;
      }
      // For other errors (network, etc.), keep session
      console.warn('[Auth] Validation error:', error.message);
      return true;
    }
  }, [token, user, login, logout]);

  // Validate immediately on mount (only once)
  useEffect(() => {
    if (token && !validationRef.current) {
      validationRef.current = true;
      // Small delay to ensure app is fully loaded
      const timer = setTimeout(() => {
        validateSession();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [token, validateSession]);

  // Periodic validation every 5 minutes (more frequent to catch email verification faster)
  useEffect(() => {
    if (token) {
      intervalRef.current = setInterval(() => {
        validateSession();
      }, 5 * 60 * 1000); // 5 minutes

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [token, validateSession]);

  // Also validate when window regains focus (user returns to tab)
  useEffect(() => {
    const handleFocus = () => {
      if (token) {
        validateSession();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [token, validateSession]);

  return { validateSession };
};

export default useAuthValidation;
